import { useRef } from 'react'
import { motion } from 'framer-motion'
import PageLayout from '../components/PageLayout'
import PageHero from '../components/PageHero'
import { useInView } from '../hooks/useInView'

const openings = [
  {
    title: 'Senior Product Engineer',
    team: 'Engineering',
    location: 'Remote · US / EU',
    type: 'Full-time',
    summary:
      'Build end-to-end on the core marketplace: listings, payments, messaging. You will ship with a small team that owns product and infra together.',
  },
  {
    title: 'Platform Engineer, Payments',
    team: 'Engineering',
    location: 'Remote · US',
    type: 'Full-time',
    summary:
      'Own the Stripe Connect integration and payout infrastructure. Deep familiarity with money movement, reconciliation, and marketplace edge cases.',
  },
  {
    title: 'ML Engineer, Cortex',
    team: 'Engineering',
    location: 'Remote · Global',
    type: 'Full-time',
    summary:
      'Turn transaction and listing data into the pricing and demand signals our operators rely on. Product-shaped ML, not research ML.',
  },
  {
    title: 'Designer, Marketplace Surface',
    team: 'Design',
    location: 'Remote · US / EU',
    type: 'Full-time',
    summary:
      'Design the components every moderator inherits when they launch their marketplace. Systems thinking with strong typographic and motion taste.',
  },
  {
    title: 'Community Success Lead',
    team: 'Operations',
    location: 'Remote · US',
    type: 'Full-time',
    summary:
      'Partner with our largest operators on launch, growth, and retention. You have run a community before and know what moderators actually need.',
  },
  {
    title: 'Developer Relations',
    team: 'Growth',
    location: 'Remote · US / EU',
    type: 'Full-time',
    summary:
      'Help moderators and external developers build on Oak. SDK samples, documentation, and the public face of our API.',
  },
]

const perks = [
  {
    title: 'Fully remote, time-zone friendly',
    body: 'We hire across the US and EU. Core overlap hours are kept light; deep work blocks are protected.',
  },
  {
    title: 'Ownership from day one',
    body: 'Meaningful equity, transparent bands, and the scope to ship things you point at years later.',
  },
  {
    title: 'Health and wellbeing',
    body: 'Top-tier medical, dental, and vision in the US; equivalent stipends elsewhere. Generous parental leave.',
  },
  {
    title: 'Gear and workspace',
    body: 'The hardware you need, a home-office setup budget, and a coworking stipend if you prefer an office.',
  },
  {
    title: 'Unmetered time off',
    body: 'Four weeks minimum, no cap. We take it seriously and track the floor, not the ceiling.',
  },
  {
    title: 'Learning budget',
    body: 'Annual budget for books, courses, conferences, and anything that sharpens the way you work.',
  },
]

const hiringSteps = [
  {
    title: 'Intro call',
    body: '30 minutes with someone from the team. We learn what you care about, you learn what we are building and why.',
  },
  {
    title: 'Craft conversation',
    body: 'A 60–90 minute working session grounded in real problems Oak is solving. No puzzles, no whiteboard gotchas.',
  },
  {
    title: 'Team round',
    body: 'Two to three conversations with people you would work alongside. Focus on collaboration, trade-offs, and how you think.',
  },
  {
    title: 'Decision',
    body: 'Offers go out within a few days of the final round. We share feedback either way — fast, direct, and useful.',
  },
]

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.38, 0.005, 0.215, 1] as const },
  },
}

function PerksSection() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { threshold: 0.2 })

  return (
    <section ref={ref} className="bg-oak-cream px-6 py-20 md:px-16 md:py-28">
      <div className="mx-auto max-w-content">
        <div className="max-w-[640px]">
          <span className="text-sm font-semibold uppercase tracking-widest text-gold-500">
            What you get
          </span>
          <h2 className="mt-4 font-serif text-[14vw] leading-[1] tracking-[-1.5px] md:text-[clamp(32px,5vw,56px)] text-oak-black">
            A small team, meaningful ownership, real craft.
          </h2>
        </div>
        <motion.div
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
          className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8"
        >
          {perks.map((p) => (
            <motion.div
              key={p.title}
              variants={fadeUp}
              className="rounded-3xl border border-oak-black/5 bg-white p-8"
            >
              <h3 className="text-[clamp(18px,2vw,22px)] font-semibold leading-tight tracking-[-0.3px] text-oak-black">
                {p.title}
              </h3>
              <p className="mt-3 text-[clamp(14px,1.3vw,16px)] leading-[1.55] tracking-[-0.2px] text-oak-black/70">
                {p.body}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

function OpeningsSection() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { threshold: 0.1 })

  return (
    <section ref={ref} id="openings" className="rounded-section bg-white px-6 py-20 md:px-16 md:py-28">
      <div className="mx-auto max-w-content">
        <div className="max-w-[640px]">
          <span className="text-sm font-semibold uppercase tracking-widest text-gold-500">
            Open roles
          </span>
          <h2 className="mt-4 font-serif text-[14vw] leading-[1] tracking-[-1.5px] md:text-[clamp(32px,5vw,56px)] text-oak-black">
            We hire slowly, on purpose.
          </h2>
          <p className="mt-6 text-[clamp(16px,1.5vw,19px)] leading-[1.5] tracking-[-0.2px] text-oak-black/60">
            Every role here has a clear charter and someone who will work closely with you from day one. Do not see your role? Write to us anyway.
          </p>
        </div>
        <motion.div
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
          className="mt-14 divide-y divide-oak-black/10 border-t border-oak-black/10"
        >
          {openings.map((role) => (
            <motion.a
              key={role.title}
              variants={fadeUp}
              href="mailto:careers@oakplatforms.com"
              className="group flex flex-col gap-4 py-8 transition-colors hover:bg-oak-cream/50 md:flex-row md:items-center md:justify-between md:px-6"
            >
              <div className="max-w-[520px]">
                <h3 className="text-[clamp(20px,2.2vw,26px)] font-semibold leading-tight tracking-[-0.4px] text-oak-black">
                  {role.title}
                </h3>
                <p className="mt-2 text-[clamp(14px,1.3vw,16px)] leading-[1.5] tracking-[-0.2px] text-oak-black/60">
                  {role.summary}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-oak-cream px-4 py-1.5 text-[13px] font-medium tracking-[-0.2px] text-oak-black/70">
                  {role.team}
                </span>
                <span className="rounded-full bg-oak-cream px-4 py-1.5 text-[13px] font-medium tracking-[-0.2px] text-oak-black/70">
                  {role.location}
                </span>
                <span className="rounded-full bg-oak-cream px-4 py-1.5 text-[13px] font-medium tracking-[-0.2px] text-oak-black/70">
                  {role.type}
                </span>
                <span className="ml-2 hidden h-10 w-10 items-center justify-center rounded-full bg-oak-black text-white transition-transform group-hover:translate-x-1 md:inline-flex">
                  →
                </span>
              </div>
            </motion.a>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

function HiringSection() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { threshold: 0.2 })

  return (
    <section ref={ref} className="bg-oak-cream px-6 py-20 md:px-16 md:py-28">
      <div className="mx-auto max-w-content">
        <div className="max-w-[640px]">
          <span className="text-sm font-semibold uppercase tracking-widest text-gold-500">
            How we hire
          </span>
          <h2 className="mt-4 font-serif text-[14vw] leading-[1] tracking-[-1.5px] md:text-[clamp(32px,5vw,56px)] text-oak-black">
            Four steps. No surprises.
          </h2>
        </div>
        <motion.div
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8"
        >
          {hiringSteps.map((step, i) => (
            <motion.div
              key={step.title}
              variants={fadeUp}
              className="rounded-3xl border border-oak-black/5 bg-white p-8 md:p-10"
            >
              <span className="text-sm font-semibold uppercase tracking-widest text-gold-500">
                Step 0{i + 1}
              </span>
              <h3 className="mt-3 text-[clamp(20px,2.2vw,26px)] font-semibold leading-tight tracking-[-0.4px] text-oak-black">
                {step.title}
              </h3>
              <p className="mt-4 text-[clamp(15px,1.4vw,17px)] leading-[1.55] tracking-[-0.2px] text-oak-black/70">
                {step.body}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

function ContactCTA() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { threshold: 0.3 })

  return (
    <section ref={ref} className="bg-oak-black px-6 py-20 md:px-16 md:py-24">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="mx-auto max-w-3xl text-center"
      >
        <h2 className="font-serif text-[14vw] leading-[1] tracking-[-1.5px] md:text-[clamp(32px,5vw,56px)] text-white">
          Do not see your role?
        </h2>
        <p className="mx-auto mt-6 max-w-[480px] text-[clamp(16px,1.5vw,19px)] leading-[1.5] tracking-[-0.2px] text-white/60">
          Send us a note. Tell us what you are working on and where you would want to plug in.
        </p>
        <a
          href="mailto:careers@oakplatforms.com"
          className="mt-10 inline-flex items-center gap-3 rounded-pill bg-white px-7 py-4 text-[clamp(16px,1.5vw,20px)] font-medium tracking-[-0.4px] text-oak-black transition-opacity hover:opacity-80"
        >
          careers@oakplatforms.com
          <span className="h-7 w-7 rounded-full bg-oak-green" />
        </a>
      </motion.div>
    </section>
  )
}

export default function CareersPage() {
  return (
    <PageLayout>
      <PageHero
        eyebrow="Careers"
        title="Build the rails for the next generation of community marketplaces."
        description="Small team. High ownership. The kind of place where the work you do is visible and the product changes because of it."
      >
        <a
          href="#openings"
          className="inline-flex items-center gap-3 rounded-pill border border-oak-black/10 bg-oak-black px-7 py-4 text-[clamp(16px,1.5vw,20px)] font-medium tracking-[-0.4px] text-white transition-opacity hover:opacity-80"
        >
          See open roles
          <span className="h-8 w-8 rounded-full bg-oak-green" />
        </a>
      </PageHero>
      <PerksSection />
      <OpeningsSection />
      <HiringSection />
      <ContactCTA />
    </PageLayout>
  )
}
